import { useState, type ReactNode } from "react";

export interface PortalEntry {
  id: string;
  children: ReactNode;
}

export interface ScreenLayoutSnapshot {
  directActionsCount: number;
  portalHostCount: number;
  portalEntries: PortalEntry[];
  keyboardAwareBodyCount: number;
  keyboardAvoidingDisabled: boolean;
  actionsHeight: number;
}

export interface ScreenLayoutStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => ScreenLayoutSnapshot;
  registerDirectActions: () => () => void;
  registerPortalHost: () => () => void;
  upsertPortalEntry: (id: string, children: ReactNode) => void;
  removePortalEntry: (id: string) => void;
  registerKeyboardAwareBody: () => () => void;
  setKeyboardAvoidingDisabled: (disabled: boolean) => void;
  setActionsHeight: (height: number) => void;
}

export const EMPTY_SNAPSHOT: ScreenLayoutSnapshot = {
  directActionsCount: 0,
  portalHostCount: 0,
  portalEntries: [],
  keyboardAwareBodyCount: 0,
  keyboardAvoidingDisabled: false,
  actionsHeight: 0,
};

/**
 * Per-ScreenLayout external store.
 *
 * Portal state lives outside React state on purpose. If it lived in a
 * shared ancestor's `useState`, `ActionsContent` — which both writes
 * portal entries and reads the context — would re-render on its own
 * write, re-run its effect, and write again: an infinite loop
 * (`children` is always a fresh JSX reference).
 *
 * With an external store only the components that explicitly subscribe
 * (`ActionsPortal`, the dev-warnings probe) re-render on a write.
 * `ActionsContent` writes but never subscribes, so it cannot re-trigger
 * itself.
 */
export function createScreenLayoutStore(): ScreenLayoutStore {
  let snapshot: ScreenLayoutSnapshot = EMPTY_SNAPSHOT;
  const listeners = new Set<() => void>();

  const set = (next: ScreenLayoutSnapshot) => {
    snapshot = next;
    listeners.forEach((listener) => listener());
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => snapshot,
    registerDirectActions() {
      set({ ...snapshot, directActionsCount: snapshot.directActionsCount + 1 });
      return () =>
        set({
          ...snapshot,
          directActionsCount: Math.max(0, snapshot.directActionsCount - 1),
        });
    },
    registerPortalHost() {
      set({ ...snapshot, portalHostCount: snapshot.portalHostCount + 1 });
      return () =>
        set({
          ...snapshot,
          portalHostCount: Math.max(0, snapshot.portalHostCount - 1),
        });
    },
    upsertPortalEntry(id, children) {
      set({
        ...snapshot,
        portalEntries: [
          ...snapshot.portalEntries.filter((entry) => entry.id !== id),
          { id, children },
        ],
      });
    },
    removePortalEntry(id) {
      set({
        ...snapshot,
        portalEntries: snapshot.portalEntries.filter((entry) => entry.id !== id),
      });
    },
    registerKeyboardAwareBody() {
      set({ ...snapshot, keyboardAwareBodyCount: snapshot.keyboardAwareBodyCount + 1 });
      return () =>
        set({
          ...snapshot,
          keyboardAwareBodyCount: Math.max(0, snapshot.keyboardAwareBodyCount - 1),
        });
    },
    setKeyboardAvoidingDisabled(disabled) {
      if (snapshot.keyboardAvoidingDisabled === disabled) return;
      set({ ...snapshot, keyboardAvoidingDisabled: disabled });
    },
    setActionsHeight(height) {
      if (snapshot.actionsHeight === height) return;
      set({ ...snapshot, actionsHeight: height });
    },
  };
}

/** Creates a stable per-instance store for the ScreenLayout root. */
export function useScreenLayoutStoreInstance(): ScreenLayoutStore {
  const [store] = useState(createScreenLayoutStore);
  return store;
}
